import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../utils/format';

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children,
  closeOnBackdrop = true,
  hideClose = false,
}) {
  const lastFocused = useRef(null);
  const dialogRef = useRef(null);
  // Latest onClose without re-running the open/close effect: callers often
  // pass an inline function, and re-running would steal focus on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    lastFocused.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    const focusable = dialogRef.current?.querySelector(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus?.();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
      if (lastFocused.current && lastFocused.current.focus) {
        try { lastFocused.current.focus(); } catch { /* ignore */ }
      }
    };
  }, [isOpen]);

  const handleBackdrop = useCallback(
    (e) => {
      if (e.target === e.currentTarget && closeOnBackdrop) onClose?.();
    },
    [closeOnBackdrop, onClose]
  );

  if (!isOpen) return null;

  const node = (
    <div className="modal-backdrop" onMouseDown={handleBackdrop} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={classNames('modal-dialog', `modal-${size}`)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || !hideClose) && (
          <div className="modal-header">
            <div>
              {title && <div className="modal-title">{title}</div>}
              {subtitle && <div className="modal-subtitle">{subtitle}</div>}
            </div>
            {!hideClose && (
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
                ×
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
